import { DataGrid, DataGridProps } from "@material-ui/data-grid";
import React, { useState } from "react";
import { IconButton, makeStyles, TextField } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";

const escapeRegExp = (value: string) => {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(0.5, 0.5, 0),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  textField: {
    border: 0,
    borderRadius: "30px",
    backgroundColor: "#F4F7FE !important",
    padding: "6px",
    margin: theme.spacing(1, 0.5, 1.5),
    "& .MuiInputBase-root": {
      "&:hover": {
        border: "0px !important",
      },
    },
    "& .MuiSvgIcon-root": {
      color: "#2B3674", // Search icon color
      marginRight: theme.spacing(0.5),
    },
    "& input": {
      color: "#2B3674",
    },
  },
  dataGrid: {
    border: 0,
    "& .MuiDataGrid-cell": {
      maxHeight: " unset !important",
      height: "auto !important",
    },
    "& .MuiDataGrid-row": {
      maxHeight: "unset !important",
      height: "auto !important",
    },
    "& .MuiDataGrid-viewport": {
      maxHeight: "unset !important",
    },
    "& .MuiDataGrid-renderingZone": {
      maxHeight: "unset !important",
    },
  },
}));

const QuickSearchToolbar = (props: any) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <TextField
        variant="standard"
        value={props.value}
        onChange={props.onChange}
        placeholder="Search…"
        className={classes.textField}
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" />,
          endAdornment: (
            <IconButton
              title="Clear"
              aria-label="Clear"
              size="small"
              style={{ visibility: props.value ? "visible" : "hidden" }}
              onClick={props.clearSearch}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          ),
        }}
      />

      {props.actions && props.actions}
    </div>
  );
};

interface CustomMuiDataGridProps extends DataGridProps {
  columnsToSearch: string[];
  actions?: any;
}

const CustomMuiDataGrid: React.FC<CustomMuiDataGridProps> = (props) => {
  const classes = useStyles();

  const { columnsToSearch, actions, ...rest } = props;

  const [searchText, setSearchText] = useState("");
  const [filteredRows, setFilteredRows] = useState([]);

  const requestSearch = (searchValue: string) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), "i");

    const filteredRows = rest.rows.filter((row: any) => {
      return columnsToSearch.some((field) => {
        return searchRegex.test(row[field]?.toString() || "");
      });
    });
    setFilteredRows(filteredRows);
  };

  return (
    <DataGrid
      {...rest}
      className={classes.dataGrid}
      rows={searchText ? filteredRows : rest.rows}
      onSelectionModelChange={(newSelectionModel: any) => {
        // Preserve selections that are currently filtered out
        const allCurrentRows = searchText ? filteredRows : rest.rows;
        const visibleIds = new Set(allCurrentRows.map((r: any) => r.id));
        const currentSelection = (rest.selectionModel as any[]) || [];

        const hiddenStillSelected = currentSelection.filter(
          (id: any) => !visibleIds.has(id),
        );
        const merged = Array.from(
          new Set([...(newSelectionModel as any[]), ...hiddenStillSelected]),
        );

        if (typeof rest.onSelectionModelChange === "function") {
          rest.onSelectionModelChange(merged);
        }
      }}
      components={{ Toolbar: QuickSearchToolbar }}
      componentsProps={{
        toolbar: {
          value: searchText,
          onChange: (event: any) => requestSearch(event.target.value),
          clearSearch: () => requestSearch(""),

          actions: actions,
        },
      }}
    />
  );
};

export default CustomMuiDataGrid;
